import styled, { css, keyframes } from 'styled-components'

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

export const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(16, 24, 32, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 1000;
  animation: ${fadeIn} 0.15s ease-out;
`

export const ModalWrapper = styled.div`
  background: #ffffff;
  border-radius: 20px;
  width: 520px;
  max-width: 100%;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  box-shadow: 0 24px 64px rgba(16, 24, 32, 0.24);
`

export const ModalContent = styled.div`
  padding: 40px 32px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  ${({ $compact }) => $compact && css`
    padding: 24px 32px 28px;
  `}
`

export const CheckmarkCircle = styled.div`
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: var(--color-success);
  box-shadow: 0 0 0 10px var(--color-success-soft);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 10px 0 28px;

  ${({ $compact }) => $compact && css`
    width: 56px;
    height: 56px;
    box-shadow: 0 0 0 6px var(--color-success-soft);
    margin: 6px 0 16px;

    svg {
      width: 34px;
      height: 34px;
    }
  `}
`

export const CheckmarkSvg = styled.svg`
  width: 52px;
  height: 52px;
`

export const ConfirmationText = styled.div`
  h2 {
    font-size: 1.6rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    margin: 0 0 6px;
    color: var(--color-text);
  }

  p {
    font-size: 1rem;
    color: var(--color-muted);
    margin: 0;
  }
`

export const ReceiptOptions = styled.div`
  width: 100%;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--color-line);
`

export const SmsLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-muted);
  margin-bottom: 12px;
  text-align: left;
`

export const SmsSection = styled.form`
  display: flex;
  flex-direction: ${({ $row }) => $row ? 'row' : 'column'};
  gap: 12px;
  width: 100%;
`

export const PhoneInputRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;
`

export const PhoneDisplay = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 56px;
  padding: 0 16px;
  border: 1px solid ${({ $empty, theme }) => $empty ? 'var(--color-line)' : theme.colors.primary};
  border-radius: 12px;
  background: var(--color-body);
  display: flex;
  align-items: center;
  text-align: left;
  font-family: inherit;
  font-size: ${({ $empty }) => $empty ? '1rem' : '1.35rem'};
  font-weight: ${({ $empty }) => $empty ? 400 : 700};
  letter-spacing: ${({ $empty }) => $empty ? '0' : '0.06em'};
  font-variant-numeric: tabular-nums;
  color: ${({ $empty, theme }) => $empty ? 'var(--color-muted)' : 'var(--color-text)'};
  overflow: hidden;
  white-space: nowrap;
`

export const PhoneCancelBtn = styled.button`
  flex-shrink: 0;
  min-height: 56px;
  padding: 0 20px;
  border: 1px solid var(--color-line);
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  font-family: inherit;
  color: var(--color-muted);
  transition: background 0.1s;

  &:active { background: var(--color-line); }
`

export const KeypadWrapper = styled.div`
  margin-top: 4px;
`

export const ReceiptIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;

  svg {
    width: 22px;
    height: 22px;
  }
`

export const SmsSentText = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 56px;
  padding: 12px 16px;
  background: var(--color-success-soft);
  border-radius: 12px;
  color: var(--color-success);
  font-size: 1rem;
  font-weight: 600;
`

export const ConfirmationActions = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  margin-top: 20px;
`

const BaseButton = styled.button`
  min-height: 56px;
  padding: 0 20px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: transform 0.1s, background 0.1s, filter 0.1s;
  -webkit-tap-highlight-color: transparent;

  &:active {
    transform: scale(0.98);
  }
`

export const PrintButton = styled(BaseButton)`
  flex: 1;
  min-width: 0;
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 1px solid var(--color-line);
  background: #ffffff;
  color: var(--color-text);

  ${ReceiptIcon} {
    color: ${({ theme }) => theme.colors.primaryText || theme.colors.primary};
  }

  &:active {
    background: var(--color-line);
  }

  &:disabled {
    cursor: wait;
    background: var(--color-body);
    color: var(--color-muted);
    transform: none;
  }
`

export const NextProgress = styled.span`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.45);
  transform-origin: left center;
  transition: transform 1s linear;
`

export const NextButton = styled(BaseButton)`
  position: relative;
  overflow: hidden;
  flex: 1;
  min-height: 64px;
  border: none;
  background: ${({ theme }) => theme.colors.primary};
  color: #ffffff;
  font-size: 1.2rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;

  span:last-child {
    position: relative;
  }

  &:active {
    filter: brightness(0.92);
  }

  ${({ $secondary, theme }) => $secondary && css`
    min-height: 56px;
    border: 1px solid var(--color-line);
    background: #ffffff;
    color: ${theme.colors.global};
    font-size: 1.05rem;

    &:active {
      filter: none;
      background: var(--color-line);
    }
  `}
`
